import request from '../../../../core/wrappers/axios-wrapper';
import { ApiURL } from '../../../../types/constants/apiUrls/api-urls';
import { RequestType } from '../../../../types/enums/request-enums';

export type RegisterRawImageFileRequest = {
    barcode: string;
    photoTypeCode: string;
    filePath: string;
    fileName: string;
    warehouseCode?: string;
};

export type RegisterRawImageFileResponse = {
    success: boolean;
    message?: string;
    rawImageId?: number;
    photoTypeCode?: string;
    status?: string;
};

export type UpdateRawImagePhotoTypeRequest = {
    rawImageId: number;
    barcode: string;
    photoTypeCode: string;
};

export type UpdateRawImagePhotoTypeResponse = {
    success: boolean;
    message?: string;
    rawImageId?: number;
    photoTypeCode?: string;
};

export type CompleteRawImageUploadRequest = {
    barcode: string;
    rawImageIds: number[];
};

export type CompleteRawImageUploadResponse = {
    success: boolean;
    message?: string;
    status?: string;
    uploadedCount?: number;
    byPhotoType?: Record<string, number>;
};

export type DeleteRawImageFileRequest = {
    rawImageId: number;
    barcode: string;
};

export type DeleteRawImageFileResponse = {
    success: boolean;
    message?: string;
};

const pickBool = (raw: Record<string, unknown>, key: string) =>
    Boolean(raw[key] ?? raw[key.charAt(0).toUpperCase() + key.slice(1)]);

const pickString = (raw: Record<string, unknown>, key: string) =>
    String(raw[key] ?? raw[key.charAt(0).toUpperCase() + key.slice(1)] ?? '');

const pickNumber = (raw: Record<string, unknown>, key: string) => {
    const value = raw[key] ?? raw[key.charAt(0).toUpperCase() + key.slice(1)];
    return value == null ? undefined : Number(value);
};

const mapRegisterResponse = (raw: Record<string, unknown>): RegisterRawImageFileResponse => ({
    success: pickBool(raw, 'success'),
    message: pickString(raw, 'message') || undefined,
    rawImageId: pickNumber(raw, 'rawImageId'),
    photoTypeCode: pickString(raw, 'photoTypeCode') || undefined,
    status: pickString(raw, 'status') || undefined,
});

const mapCompleteResponse = (raw: Record<string, unknown>): CompleteRawImageUploadResponse => {
    const byPhotoTypeRaw = (raw.byPhotoType ?? raw.ByPhotoType ?? {}) as Record<string, number>;
    return {
        success: pickBool(raw, 'success'),
        message: pickString(raw, 'message') || undefined,
        status: pickString(raw, 'status') || undefined,
        uploadedCount: pickNumber(raw, 'uploadedCount'),
        byPhotoType: byPhotoTypeRaw,
    };
};

const RegisterFile = async (data: RegisterRawImageFileRequest): Promise<RegisterRawImageFileResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.RawImageUploadCommandURL.RegisterFile,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;

    return mapRegisterResponse(raw);
};

const mapUpdatePhotoTypeResponse = (raw: Record<string, unknown>): UpdateRawImagePhotoTypeResponse => ({
    success: pickBool(raw, 'success'),
    message: pickString(raw, 'message') || undefined,
    rawImageId: pickNumber(raw, 'rawImageId'),
    photoTypeCode: pickString(raw, 'photoTypeCode') || undefined,
});

const UpdatePhotoType = async (data: UpdateRawImagePhotoTypeRequest): Promise<UpdateRawImagePhotoTypeResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.RawImageUploadCommandURL.UpdatePhotoType,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;

    return mapUpdatePhotoTypeResponse(raw);
};

const CompleteUpload = async (data: CompleteRawImageUploadRequest): Promise<CompleteRawImageUploadResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.RawImageUploadCommandURL.CompleteUpload,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;

    return mapCompleteResponse(raw);
};

const mapDeleteFileResponse = (raw: Record<string, unknown>): DeleteRawImageFileResponse => ({
    success: pickBool(raw, 'success'),
    message: pickString(raw, 'message') || undefined,
});

const DeleteFile = async (data: DeleteRawImageFileRequest): Promise<DeleteRawImageFileResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.RawImageUploadCommandURL.DeleteFile,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;

    return mapDeleteFileResponse(raw);
};

export const RawImageUploadCommand = {
    RegisterFile,
    UpdatePhotoType,
    DeleteFile,
    CompleteUpload,
};
